import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { API } from "../constants";

export default function AccountPage() {
  const { email, firstName, setFirstName, lastName, setLastName, token, getPasswordStrength, strengthColors, strengthWidths } = useApp();
  const [editFirst, setEditFirst] = useState(firstName);
  const [editLast, setEditLast] = useState(lastName);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [hasPassword, setHasPassword] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [confirmingPasswordChange, setConfirmingPasswordChange] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);

  const submitEmailChange = async () => {
    if (!newEmail.trim()) { setEmailMessage("Please enter a new email address."); return; }
    setSavingEmail(true);
    setEmailMessage("");
    try {
      const res = await fetch(`${API}/auth/change-email`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ new_email: newEmail })
      });
      const data = await res.json();
      if (res.ok) {
        setEmailMessage(`✓ ${data.message}`);
        setEditingEmail(false);
        setNewEmail("");
      } else {
        setEmailMessage(data.detail || "Failed to request email change");
      }
    } catch {
      setEmailMessage("Something went wrong");
    }
    setSavingEmail(false);
  };

  useEffect(() => {
    fetch(`${API}/user/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        setHasPassword(data.has_password ?? true);
      })
      .catch(() => {});
  }, [token]);

  const submitPasswordChange = async () => {
    if (!newPassword.trim()) { setPasswordMessage("Please enter a new password."); return; }
    if (hasPassword && !currentPassword.trim()) { setPasswordMessage("Please enter your current password."); return; }
    setSavingPassword(true);
    setPasswordMessage("");
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: hasPassword ? currentPassword : null, new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setPasswordMessage(`✓ ${data.message}`);
        setHasPassword(true);
        setCurrentPassword("");
        setNewPassword("");
        setPasswordStrength("");
      } else {
        setPasswordMessage(data.detail || "Failed to update password");
      }
    } catch {
      setPasswordMessage("Something went wrong");
    }
    setSavingPassword(false);
    setConfirmingPasswordChange(false);
  };

  const handlePasswordSubmitClick = () => {
    if (!newPassword.trim()) { setPasswordMessage("Please enter a new password."); return; }
    if (hasPassword && !currentPassword.trim()) { setPasswordMessage("Please enter your current password."); return; }
    setConfirmingPasswordChange(true);
  };

  const handleSave = async () => {
    setSaving(true); setMessage("");
    try {
      const res = await fetch(`${API}/user/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: editFirst, last_name: editLast })
      });
      if (res.ok) {
        setFirstName(editFirst);
        setLastName(editLast);
        setMessage("✓ Profile updated successfully");
      } else {
        setMessage("Failed to update profile");
      }
    } catch { setMessage("Something went wrong"); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: "600px" }}>
      <h2 style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", margin: "0 0 4px 0" }}>Account Details</h2>
      <p style={{ fontSize: "14px", color: "var(--text-tertiary)", margin: "0 0 32px 0" }}>Manage your personal information.</p>

      {/* Avatar preview */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "var(--navy)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", color: "white", fontWeight: "600" }}>
          {editFirst && editLast ? `${editFirst.charAt(0)}${editLast.charAt(0)}`.toUpperCase() : email?.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-primary)" }}>
            {editFirst || editLast ? `${editFirst} ${editLast}`.trim() : "No name set"}
          </div>
          <div style={{ fontSize: "13px", color: "var(--text-tertiary)" }}>{email}</div>
        </div>
      </div>

      {/* Name fields */}
      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden", marginBottom: "16px" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Profile</div>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <label style={{ fontSize: "12px", fontWeight: "500", color: "var(--text-secondary)", display: "block", marginBottom: "6px" }}>First Name</label>
            <input type="text" value={editFirst} onChange={e => setEditFirst(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "0.5px solid var(--border)", fontSize: "14px", outline: "none", background: "var(--bg-secondary)", color: "var(--text-primary)", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: "12px", fontWeight: "500", color: "var(--text-secondary)", display: "block", marginBottom: "6px" }}>Last Name</label>
            <input type="text" value={editLast} onChange={e => setEditLast(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "0.5px solid var(--border)", fontSize: "14px", outline: "none", background: "var(--bg-secondary)", color: "var(--text-primary)", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: "12px", fontWeight: "500", color: "var(--text-secondary)", display: "block", marginBottom: "6px" }}>Email Address</label>
            <input type="text" value={email} disabled
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "0.5px solid var(--border)", fontSize: "14px", outline: "none", background: "var(--bg-tertiary)", color: "var(--text-tertiary)", boxSizing: "border-box", cursor: "not-allowed" }} />
            {editingEmail ? (
              <div style={{ marginTop: "8px" }}>
                <input type="email" placeholder="New email address" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "0.5px solid var(--border)", fontSize: "14px", outline: "none", background: "var(--bg-secondary)", color: "var(--text-primary)", boxSizing: "border-box", marginBottom: "8px" }} />
                {emailMessage && <div style={{ fontSize: "13px", color: emailMessage.startsWith("✓") ? "var(--success)" : "var(--danger)", marginBottom: "8px" }}>{emailMessage}</div>}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => { setEditingEmail(false); setNewEmail(""); setEmailMessage(""); }}
                    style={{ padding: "8px 16px", background: "transparent", border: "0.5px solid var(--border)", borderRadius: "8px", fontSize: "13px", cursor: "pointer", color: "var(--text-secondary)" }}>
                    Cancel
                  </button>
                  <button onClick={submitEmailChange} disabled={savingEmail}
                    style={{ padding: "8px 16px", background: savingEmail ? "var(--border)" : "var(--navy)", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "500", cursor: savingEmail ? "default" : "pointer" }}>
                    {savingEmail ? "Sending..." : "Send Confirmation Link"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: "6px" }}>
                {emailMessage && <div style={{ fontSize: "13px", color: emailMessage.startsWith("✓") ? "var(--success)" : "var(--danger)", marginBottom: "6px" }}>{emailMessage}</div>}
                <span onClick={() => setEditingEmail(true)} style={{ fontSize: "12px", color: "var(--navy)", cursor: "pointer", fontWeight: "500" }}>
                  Change email address
                </span>
              </div>
            )}
          </div>
          {message && <div style={{ fontSize: "13px", color: message.startsWith("✓") ? "var(--success)" : "var(--danger)" }}>{message}</div>}
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "10px 24px", background: saving ? "var(--border)" : "var(--navy)", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "500", cursor: saving ? "default" : "pointer", alignSelf: "flex-start" }}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Security / Password */}
      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden", marginBottom: "16px" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Security</div>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {hasPassword && (
            <div>
              <label style={{ fontSize: "12px", fontWeight: "500", color: "var(--text-secondary)", display: "block", marginBottom: "6px" }}>Current Password</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "0.5px solid var(--border)", fontSize: "14px", outline: "none", background: "var(--bg-secondary)", color: "var(--text-primary)", boxSizing: "border-box" }} />
            </div>
          )}
          <div>
            <label style={{ fontSize: "12px", fontWeight: "500", color: "var(--text-secondary)", display: "block", marginBottom: "6px" }}>
              {hasPassword ? "New Password" : "Create a Password"}
            </label>
            <input type="password" value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setPasswordStrength(getPasswordStrength(e.target.value)); }}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "0.5px solid var(--border)", fontSize: "14px", outline: "none", background: "var(--bg-secondary)", color: "var(--text-primary)", boxSizing: "border-box" }} />
            {passwordStrength && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                <div style={{ flex: 1, height: "4px", background: "var(--border)", borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: strengthWidths[passwordStrength], background: strengthColors[passwordStrength], borderRadius: "2px", transition: "all 0.3s" }} />
                </div>
                <span style={{ fontSize: "12px", color: strengthColors[passwordStrength], minWidth: "36px" }}>{passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}</span>
              </div>
            )}
            {!hasPassword && (
              <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "6px" }}>
                Your account currently only signs in with Google. Setting a password lets you also log in with your email address.
              </div>
            )}
          </div>
          {passwordMessage && <div style={{ fontSize: "13px", color: passwordMessage.startsWith("✓") ? "var(--success)" : "var(--danger)" }}>{passwordMessage}</div>}

          {confirmingPasswordChange ? (
            <div style={{ padding: "12px 14px", background: "var(--bg-tertiary)", borderRadius: "8px", border: "0.5px solid var(--border)" }}>
              <p style={{ fontSize: "13px", color: "var(--text-primary)", margin: "0 0 10px 0" }}>
                {hasPassword
                  ? "Your old password will stop working immediately once changed. Continue?"
                  : "This will set a password for your account. Continue?"}
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => setConfirmingPasswordChange(false)}
                  style={{ padding: "8px 16px", background: "transparent", border: "0.5px solid var(--border)", borderRadius: "8px", fontSize: "13px", cursor: "pointer", color: "var(--text-secondary)" }}>
                  Cancel
                </button>
                <button onClick={submitPasswordChange} disabled={savingPassword}
                  style={{ padding: "8px 16px", background: savingPassword ? "var(--border)" : "var(--navy)", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "500", cursor: savingPassword ? "default" : "pointer" }}>
                  {savingPassword ? "Saving..." : "Confirm"}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={handlePasswordSubmitClick}
              style={{ padding: "10px 24px", background: "var(--navy)", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "500", cursor: "pointer", alignSelf: "flex-start" }}>
              {hasPassword ? "Change Password" : "Set Password"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}